import React from 'react'
import {Translate} from 'react-localize-redux';
import { PERSONAL_DATA_FIELDS, encryptData, decryptData, generateKey, postErrorSlack } from '../../../../utils/index.jsx';
import Form from '../../../general/form';
import { Grid, Typography } from '@mui/material';
import { normalizePatientFormValues } from '../../../../utils/normalizePatientFormValues';

export default function PersonalDataForm(props) {
    const orderedFields = React.useMemo(() => {
        return props.fields.sort((a,b) => a.order - b.order);
    }, [props.fields]);
    const form = React.useMemo(() => {
        let formObj = {};
        for (let i = 0; i < orderedFields.length; i++) {
            const value = orderedFields[i];
            formObj[value.name] = orderedFields[i];
        }
        return formObj;
    }, [orderedFields]);

    // SHER-435: surface the missing-encryption-key failure instead of the save
    // button silently doing nothing. Rendered inside the form via Form's
    // `externalError` so the user keeps what they typed.
    // See docs/features/observability-auth/encryption-key-guards.md.
    const [errorEncryption, setErrorEncryption] = React.useState(false);

    //Se generan las claves del paciente, tanto para el researcher como para el posterior acceso del paciente
    async function encryptPersonalData(data){
        normalizePatientFormValues(data, props.fields);
        // encryptPersonalData uses rawKeyResearcher directly (password was used
        // historically to derive it; that work now happens once at login and the
        // result is cached). The guard used to check "password" which was a
        // false-positive for users who signed in through /[type]/login
        // (src/components/dashboard/login.jsx) — that path sets jwt +
        // rawKeyResearcher but not password. Check the thing we actually need.
        const rawKeyResearcher = localStorage.getItem("rawKeyResearcher");
        if(!rawKeyResearcher){
            console.error("No rawKeyResearcher — session missing encryption keys");
            postErrorSlack("/add-patient", new Error("No rawKeyResearcher in localStorage"), { reason: "missing-key" });
            setErrorEncryption(true);
            return "error";
        }
        // Checked here rather than at mount: a mount guard would hide the form
        // from sessions that never tried to write, and e2e/add-patient.spec.ts
        // asserts it renders. Note this path never applies the
        // encryptedKeyUsed===0 / default-password fallback that the read path
        // (getInvestigationRawKey) has, so such investigations cannot save
        // personal data at all — a pre-existing gap, not something this guard
        // introduces. The copy therefore stays generic about the remedy.
        // Clear a previous error: the key can come back mid-session (localStorage
        // is shared, so re-logging in another tab restores it), and a stale
        // message next to a save that just succeeded is worse than none.
        setErrorEncryption(false);

        const rawKeyInvestigation = decryptData(props.keyResearcherInvestigation, rawKeyResearcher);

        let encryptedData = [];
       
        
        for (const key of Object.keys(data)) {
            let value = data[key];
            const fieldForm = props.fields.find(field => field.name === key);
            if(fieldForm){
                if(fieldForm.encrypted !== false){
                    // Skip null/undefined values — avoids `value.getMonth` throwing
                    // "Cannot read properties of undefined (reading 'getMonth')" on
                    // optional fields left blank. The required-field gate lives at
                    // the form layer (`fieldForm.required`); nothing to encrypt here.
                    if(value == null){
                        continue;
                    }
                    if(typeof value.getMonth === 'function'){
                        value = value.getTime().toString();
                    }
                    // Date-only fields: store as UTC-midnight ms so the same calendar day everywhere.
                    // Must keep ms-epoch on the wire: decryptSinglePatientData round-trips this as
                    // parseInt(...) → new Date(ms). A YYYY-MM-DD string survives decrypt as year
                    // (e.g. parseInt("1985-03-15") === 1985 → new Date(1985) === 1970-01-01), which
                    // is what SHER-193 reported after FieldSherwood switched to .format("YYYY-MM-DD").
                    if(fieldForm.type === 'date'){
                        if(typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))){
                            const d = new Date(Number(value));
                            value = String(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
                        } else if(typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)){
                            const [y, m, day] = value.split('-').map(Number);
                            value = String(Date.UTC(y, m - 1, day));
                        }
                    }
                    value = encryptData(value.toString(), rawKeyInvestigation);
                }
                const field = form[key];
                field.value = value;
                encryptedData.push(field) 
            }
            else{
                postErrorSlack("", "fieldForm no found:"+key , "");
            } 
        }
        
        //Generación de claves de paciente de Sherwood
        const rawKeyPatient = import.meta.env.VITE_APP_DEFAULT_PATIENT_PASSWORD;// o la clave que se imprime
        const patientKeyEncrInvestigation = encryptData(rawKeyInvestigation, rawKeyPatient);

        props.callBackForm({
            "keyPatInv" : patientKeyEncrInvestigation,
            "personalData" : encryptedData
        }, data);
        // //TEST
        // for (const key of Object.keys(encryptedData)) {
        //     const decriptedValue =  decryptData(encryptedData[key], rawPatientKeyResearcher);
        //     console.log("Decripted "+key+" "+decriptedValue);
        // }
    }
    function renderHeader(){
        if(props.hospital){
            return(
                <Typography variant="body2" color="textPrimary">
                    <Translate id="hospital.patient.fill-in" />
                </Typography>
            )
        }
        else{
            return(
                <React.Fragment>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" color="textPrimary">
                            <Translate id="investigation.fill.personal_data.title" />
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textPrimary">
                            <Translate id="investigation.fill.personal_data.description" />
                        </Typography>
                    </Grid>
                </React.Fragment>
            )
        }
    }
    const personalFields = {};
    
    if(JSON.stringify(form) === '{}'){
        return "LOading"
    }
    return (
        <Grid container spacing={1}>
            <Grid item xs={12}>
                {renderHeader()}
            </Grid>
            <Grid item xs={12}>
                <Form fields={form} initialData={props.initialData} submitText={props.submitText}
                    externalError={errorEncryption ? "investigation.fill.personal_data.error.missing_key" : undefined}
                    callBackForm={(data) => encryptPersonalData(data)}/>
            </Grid>
        </Grid>
    )    
}
